import { trackEvent as fireTrackEvent } from 'ddc-track-event';
import logger from 'ws-scripts/modules/logger';

/**
 * Vocabulary and dispatcher for drawer analytics events.
 *
 * Attribute-by-attribute reference, the rationale behind this event shape, and
 * the open questions for analytics all live in `docs/analytics-event-schema.md`.
 */

/** Fallback only — the real value comes from SSR `requestData.widgetName`. */
export const TRACK_WIDGET_NAME = 'ws-navigation-drawer';

/**
 * The `action` value for every event this widget emits. Constant because in the
 * DDC schema `action` is *how* the user interacted, not what happened.
 */
export const TRACK_ACTION = 'clicked';

/**
 * Identifies which drawer interaction an event represents — the attribute to
 * facet on, since `action` is always 'clicked'. See "A caveat on `drawerEvent`
 * casing, and why this attribute exists" in the schema doc.
 */
export const DRAWER_EVENT = {
	DRAWER_OPEN: 'drawerOpen',
	DRAWER_CLOSE: 'drawerClose',
	NAV_LINK_CLICK: 'navLinkClick',
	FEATURED_ITEM_CLICK: 'featuredItemClick',
	SUBNAV_DRILLDOWN: 'subNavDrilldown',
	CONTENT_ZONE_INTERACTION: 'contentZoneInteraction'
};

/**
 * `widgetState` — the widget's state *before* the action. Anything reachable
 * only once the drawer is open reports DRAWER_OPEN; the hamburger and the
 * featured bar (which sits outside the drawer) report DEFAULT.
 */
export const WIDGET_STATE = {
	DEFAULT: 'default',
	DRAWER_OPEN: 'Nav Drawer Open'
};

/**
 * `element` and `result` strings, gathered here so a rename is one edit per
 * string rather than one per call site.
 *
 * `getHeaderEl`, `getNavigatedResult` and `megaMenuOpenedResult` are copied
 * verbatim from ws-navigation's `TRACKING_CONFIG` (`src/utils/tracking.js`) —
 * do not reword them, or drawer links stop aggregating with the nav types the
 * drawer replaces, which is the point of CR-7. The drawer-specific forms have no
 * upstream equivalent and are the ones analytics may want renamed.
 *
 * `closeMethod` and `interactionType` are deliberately duplicated into `element`
 * (as well as being attributes) because the legacy GA path drops every custom
 * attribute. See "Two delivery paths" in the schema doc for what survives where,
 * and why `depth` and `direction` are not duplicated.
 */
export const TRACKING_CONFIG = {
	getHeaderEl: (itemText) => `Sitewide Header Click - ${itemText}`,
	getNavigatedResult: (url) => `Navigated to ${url}`,
	megaMenuOpenedResult: 'Mega Menu Opened',
	// Ours, not ws-navigation's: the trigger toggles, so without this a dashboard
	// counting opens would count closes too.
	megaMenuClosedResult: 'Mega Menu Closed',
	hamburgerEl: 'Nav Drawer Hamburger',
	getCloseEl: (closeMethod) => `Nav Drawer Close - ${closeMethod}`,
	drawerOpenedResult: 'Nav Drawer Opened',
	drawerClosedResult: 'Nav Drawer Closed',
	getDrilldownEl: (itemText) => `Nav Drawer Drilldown - ${itemText}`,
	subNavOpenedResult: 'Sub Navigation Opened',
	getContentZoneEl: (fragmentType, interactionType) =>
		`Nav Drawer Content Zone - ${fragmentType} - ${interactionType}`,
	contentZoneResult: 'Content Zone Interaction'
};

/** How the drawer was dismissed, reported as `closeMethod` on `drawerClose`. */
export const CLOSE_METHOD = {
	CLOSE_BUTTON: 'close_button',
	ESCAPE: 'escape',
	BACKDROP: 'backdrop',
	NAV_LINK: 'nav_link'
};

/** Where a NavFragment content zone sits relative to its sibling nav links. */
export const ZONE_POSITION = {
	ABOVE: 'above',
	BELOW: 'below'
};

/**
 * Value reported when a discriminator cannot be determined. Emitted rather than
 * omitted so the attribute is always present and queryable.
 */
export const UNKNOWN = 'unknown';

/**
 * Derives the `fragmentType` for a content zone from its fragment URL (CR-6) —
 * the CMS's own identifier for what was embedded, and already in hand as
 * `DrawerNavFragment`'s `url` prop, so no DOM read is needed at click time.
 *
 * Deliberately NOT derived from `FRAGMENT_CLASS_MAP`; the schema doc records why
 * (that map is a styling policy, and its keys are not all types).
 *
 * @param {string|undefined} url - the fragment URL
 * @returns {string} slug identifying the fragment, or 'unknown'
 */
export const resolveFragmentType = (url) => {
	if (typeof url !== 'string' || url.trim() === '') return UNKNOWN;
	const path = url.trim().split(/[?#]/)[0].replace(/\/+$/, '');
	const basename = path.slice(path.lastIndexOf('/') + 1);
	// Drop the extension CMS fragment paths carry (`.htm`, `.html`).
	const slug = basename.replace(/\.[^.]*$/, '').toLowerCase();
	return slug === '' ? UNKNOWN : slug;
};

/** Kind of element the user activated inside a content zone. */
export const INTERACTION_TYPE = {
	SEARCH: 'search',
	CTA: 'cta',
	PHONE: 'phone',
	LINK: 'link'
};

/**
 * Navigation depth reported as `depth`. Dealer navigation trees are 2 levels
 * deep in practice (FR-6, AC-6.3), so these are the only two values emitted.
 */
export const NAV_DEPTH = {
	TOP_LEVEL: 1,
	SUB_NAV: 2
};

/**
 * Normalizes the SSR-detected device type into the lowercase form analytics
 * reports on. Note that `requestData.deviceType` in practice only carries
 * 'MOBILE' or 'DESKTOP' — see the `deviceType` caveat in the schema doc before
 * building a tablet segment.
 *
 * @param {string|undefined} deviceType - raw `requestData.deviceType`
 * @returns {'mobile'|'tablet'|'desktop'|'unknown'}
 */
export const normalizeDeviceType = (deviceType) => {
	if (typeof deviceType !== 'string' || deviceType.trim() === '')
		return 'unknown';
	return deviceType.trim().toLowerCase();
};

const isNonEmptyString = (value) =>
	typeof value === 'string' && value.length > 0;

/**
 * Fires one tracking event through the shared `ddc-track-event` pipeline (CR-7),
 * which calls `window.DDC.fireTrackingEvent` — the same path ws-navigation and
 * ws-model-selector use. Adds no network request of its own (CR-8).
 *
 * Non-throwing by design: analytics must never break navigation. Both guards are
 * load-bearing — `ddc-track-event` validates its identifier arguments *before*
 * its own browser check, so an unguarded call throws on the server rather than
 * no-opping, and a patched `DDC.fireTrackingEvent` would propagate.
 *
 * @param {string} widgetName - OSIRIS widget name, e.g. 'ws-navigation-drawer'
 * @param {string} widgetId - the specific widget instance, i.e. `windowId`
 * @param {Object} attributes - tracking attributes; see `docs/analytics-event-schema.md`
 * @returns {void}
 */
const trackEvent = (widgetName, widgetId, attributes) => {
	if (typeof window === 'undefined') return;

	if (!isNonEmptyString(widgetName) || !isNonEmptyString(widgetId)) {
		logger.warn(
			`[trackEvent] skipped ${attributes?.drawerEvent ?? 'event'}: ` +
				'widgetName and windowId must both be non-empty strings, got ' +
				`${JSON.stringify(widgetName)} and ${JSON.stringify(widgetId)}.`
		);
		return;
	}

	try {
		fireTrackEvent(widgetName, widgetId, attributes);
	} catch (err) {
		logger.warn('[trackEvent] could not fire tracking event:', err);
	}
};

export default trackEvent;
